import { c as _c } from "react/compiler-runtime";
// ── Support ID copy affordances ──────────────────────────────────────────────
// Two variants sharing `useCopySupportId`'s clipboard logic: the labeled
// SupportIdRow (ErrorState) and the icon-only CopySupportIdIconButton
// (MetricErrorState).
//
// SupportIdRow renders the ID as VISIBLE text under the button. It used to be
// tooltip-only, which defeated the one thing the ID is for: a user reporting a
// problem cannot hover — not on touch at all — and a screenshot never captures a
// tooltip, so the ID was unreachable in exactly the moment it is needed. A
// clipboard-permission failure now also degrades to "select the visible text",
// which is why `useCopySupportId` still needs no `onFallback`. The icon-only
// variant keeps the tooltip: it has no room for the ID. Ref: ASC-9396.

import type { ReactElement } from 'react';
import { Button } from '@oraczen/zendesign-ui/components/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@oraczen/zendesign-ui/components/tooltip';
import { Check, Copy } from 'lucide-react';
import { useCopySupportId } from './useCopySupportId';
export function SupportIdRow(t0) {
  const $ = _c(16);
  const {
    supportId
  } = t0;
  const {
    copyStatus,
    handleCopyClick
  } = useCopySupportId(supportId);
  const isCopied = copyStatus === "copied";
  let t1;
  if ($[0] !== isCopied) {
    t1 = isCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />;
    $[0] = isCopied;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isCopied ? "Copied" : "Copy Error ID";
  let t3;
  if ($[2] !== handleCopyClick || $[3] !== t1 || $[4] !== t2) {
    t3 = <Button type="button" variant="secondary" size="tiny" isCurved={true} aria-label="Copy Error ID" onClick={handleCopyClick}>{t1}{t2}</Button>;
    $[2] = handleCopyClick;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="select-none">(</span>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="select-none">)</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== supportId) {
    t6 = <span aria-hidden="true" className="textExtraSmallLeadingNormalRegular max-w-full break-all text-center text-muted-foreground">{t4}{supportId}{t5}</span>;
    $[8] = supportId;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isCopied ? "Copied" : null;
  let t8;
  if ($[10] !== t7) {
    t8 = <span aria-live="polite" role="status" className="sr-only">{t7}</span>;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t3 || $[13] !== t6 || $[14] !== t8) {
    t9 = <div className="flex flex-col items-center gap-1">{t3}{t6}{t8}</div>;
    $[12] = t3;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
export function CopySupportIdIconButton(t0) {
  const $ = _c(11);
  const {
    supportId
  } = t0;
  const {
    copyStatus,
    handleCopyClick
  } = useCopySupportId(supportId);
  const isCopied = copyStatus === "copied";
  const t1 = isCopied ? "Error ID copied" : "Copy Error ID";
  let t2;
  if ($[0] !== isCopied) {
    t2 = isCopied ? <Check className="size-4" /> : <Copy className="size-4" />;
    $[0] = isCopied;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== handleCopyClick || $[3] !== t1 || $[4] !== t2) {
    t3 = <TooltipTrigger asChild={true}><Button type="button" variant="secondary" size="iconTiny" isCurved={true} aria-label={t1} onClick={handleCopyClick}>{t2}</Button></TooltipTrigger>;
    $[2] = handleCopyClick;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== supportId) {
    t4 = <TooltipContent>{supportId}</TooltipContent>;
    $[6] = supportId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <TooltipProvider><Tooltip>{t3}{t4}</Tooltip></TooltipProvider>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}